import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Wrench, MapPin, Phone, Navigation, Clock, Send, Upload, X, BadgeCheck } from "lucide-react";
import { api, photoUrl, uploadFiles, formatApiError } from "../api";
import { TOW_STATUS, fmtDate, fmtDistance, slugify } from "../lib/labels";
import MapView from "../components/MapView";

const inputCls = "w-full px-4 py-2.5 rounded-xl bg-slate-950/70 border border-white/10 text-slate-100 text-sm focus:outline-none focus:border-cyan-400/60 transition-colors";
const labelCls = "block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5";

export default function ServisBul() {
  const [category, setCategory] = useState("");
  const [city, setCity] = useState("");
  const [district, setDistrict] = useState("");
  const [catalog, setCatalog] = useState([]);
  const [typeKey, setTypeKey] = useState("");
  const [loc, setLoc] = useState(null);
  const [services, setServices] = useState(null);
  const [myRequests, setMyRequests] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [modal, setModal] = useState(null); // service obj
  const [reqForm, setReqForm] = useState({ vehicleId: "", vin: "", brand: "", model: "", plate: "", address: "", description: "", photos: [] });
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const loadMine = () => {
    api.get("/servis/taleplerim").then((r) => setMyRequests(r.data)).catch(() => setMyRequests([]));
    api.get("/my-vehicles").then((r) => setVehicles(r.data)).catch(() => {});
  };
  useEffect(() => { loadMine(); }, []);
  useEffect(() => {
    api.get("/isletme-turleri").then((r) => setCatalog(r.data)).catch(() => {});
  }, []);

  const svcOptions = (catalog.find((c) => c.key === typeKey)?.services) || [];
  const typeLabel = catalog.find((c) => c.key === typeKey)?.label || "";

  const search = async () => {
    setServices(null);
    try {
      const params = {};
      if (city) params.city = city;
      if (district) params.district = district;
      if (category) params.service = category;
      if (loc) { params.lat = loc.lat; params.lng = loc.lng; }
      const { data } = await api.get("/servisler", { params });
      setServices(data);
    } catch (e) { toast.error(formatApiError(e)); setServices([]); }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) { toast.error("Tarayıcınız konum desteklemiyor."); return; }
    navigator.geolocation.getCurrentPosition(
      (p) => { setLoc({ lat: p.coords.latitude, lng: p.coords.longitude }); toast.success("Konumunuz alındı"); },
      () => toast.error("Konum izni verilmedi."), { timeout: 10000 });
  };

  const openModal = (s) => {
    setModal(s);
    setReqForm({ vehicleId: "", vin: "", brand: "", model: "", plate: "", address: "", description: "", photos: [] });
  };

  const pickVehicle = (vid) => {
    const v = vehicles.find((x) => x.id === vid);
    setReqForm((f) => ({ ...f, vehicleId: vid, vin: v ? v.vin : "", brand: v ? v.brand : "", model: v ? v.model : "", plate: v ? v.plate || "" : "" }));
  };

  const submitRequest = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/servis/talep", {
        service_id: modal.id, category: category || typeLabel || "Genel Servis",
        vin: reqForm.vin, brand: reqForm.brand, model: reqForm.model, plate: reqForm.plate,
        city: modal.city || city, district: modal.district || district, address: reqForm.address,
        description: reqForm.description, photos: reqForm.photos,
      });
      toast.success("Servis talebiniz iletildi");
      setModal(null);
      loadMine();
    } catch (err) { toast.error(formatApiError(err)); }
    setSending(false);
  };

  const svcMarkers = (services || []).filter((s) => s.lat && s.lng)
    .map((s) => ({ lat: s.lat, lng: s.lng, label: s.business_name, color: "#f97316" }));

  return (
    <div data-testid="servis-bul-page" className="min-h-screen pt-20 sm:pt-24 pb-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
        <div className="flex items-center gap-3">
          <Wrench className="w-6 h-6 text-cyan-300" />
          <h1 className="text-2xl sm:text-3xl font-bold text-white">Tamirci / Servis Bul</h1>
        </div>

        <div className="rounded-2xl border border-white/10 bg-slate-900/60 backdrop-blur-xl p-5 sm:p-7 space-y-4">
          <div>
            <label className={labelCls}>İşletme Türü</label>
            <select data-testid="svc-type-select" value={typeKey}
              onChange={(e) => { setTypeKey(e.target.value); setCategory(""); }}
              className={inputCls}>
              <option value="">Tümü</option>
              {catalog.filter((c) => c.family === "servis").map((c) => (
                <option key={c.key} value={c.key}>{c.label}</option>
              ))}
            </select>
          </div>
          {svcOptions.length > 0 && (
            <div>
              <label className={labelCls}>İstenen Hizmet</label>
              <div className="flex flex-wrap gap-2">
                {svcOptions.map((c) => (
                  <button key={c} type="button" data-testid={`svc-cat-${slugify(c)}`}
                    onClick={() => setCategory(category === c ? "" : c)}
                    className={`text-xs px-3.5 py-2 rounded-full border transition-colors ${
                      category === c ? "border-cyan-400/60 bg-cyan-400/15 text-cyan-300" : "border-white/10 text-slate-400 hover:text-slate-200"}`}>
                    {c}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input data-testid="svc-city" value={city} onChange={(e) => setCity(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), search())}
              className={inputCls} placeholder="İl (örn. Kocaeli)" />
            <input data-testid="svc-district" value={district} onChange={(e) => setDistrict(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), search())}
              className={inputCls} placeholder="İlçe (örn. Çayırova)" />
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex gap-2">
              <button data-testid="svc-use-location" onClick={useMyLocation}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-cyan-400/40 text-cyan-300 text-sm font-semibold hover:bg-cyan-400/10 transition-colors whitespace-nowrap">
                <Navigation className="w-4 h-4" /> {loc ? "Konum ✓" : "Konumum"}
              </button>
              <button data-testid="svc-search-button" onClick={search}
                className="px-6 py-2.5 rounded-xl bg-cyan-400 text-slate-950 font-bold text-sm hover:bg-cyan-300 transition-all whitespace-nowrap">
                Servisleri Listele
              </button>
            </div>
          </div>
          {loc && services && svcMarkers.length > 0 && (
            <MapView center={[loc.lat, loc.lng]}
              markers={[{ lat: loc.lat, lng: loc.lng, label: "Konumunuz", color: "#f59e0b" }, ...svcMarkers]} height={240} />
          )}
        </div>

        {services !== null && (
          <div data-testid="svc-results" className="space-y-3">
            {services.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-6">Kriterlere uygun müsait servis bulunamadı.</p>
            ) : services.map((s) => (
              <div key={s.id} data-testid={`svc-card-${s.id}`}
                className="rounded-2xl border border-white/10 bg-slate-900/60 backdrop-blur-xl p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-bold text-slate-100 flex items-center gap-1.5">
                      {s.logo && <img src={photoUrl(s.logo)} alt="" className="w-6 h-6 rounded-md object-cover border border-white/10" />}
                      {s.business_name}
                      {s.verified && <BadgeCheck data-testid={`svc-verified-${s.id}`} className="w-4 h-4 text-emerald-400 shrink-0" />}
                    </p>
                    {s.business_type_label && <p className="text-[11px] text-slate-500 mt-0.5">{s.business_type_label}</p>}
                    <p className="text-xs text-slate-400 mt-1 flex items-center gap-1.5">
                      <MapPin className="w-3.5 h-3.5" /> {s.city}{s.district && ` / ${s.district}`}{s.address && ` — ${s.address}`}
                    </p>
                    {s.working_hours && (
                      <p className="text-xs text-slate-400 mt-1 flex items-center gap-1.5">
                        <Clock className="w-3.5 h-3.5" /> {s.working_hours}
                      </p>
                    )}
                    {s.mesafe_km != null && (
                      <p data-testid={`svc-distance-${s.id}`} className="text-xs text-cyan-300 mt-1 font-semibold">{fmtDistance(s.mesafe_km)}</p>
                    )}
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {(s.service_types || []).map((t) => (
                        <span key={t} className="text-[10px] px-2 py-0.5 rounded-full border border-white/10 text-slate-400">{t}</span>
                      ))}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <span className="text-xs px-3 py-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-300">Müsait</span>
                    {s.phone && <p className="text-sm text-cyan-300 flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> {s.phone}</p>}
                    <button data-testid={`svc-request-${s.id}`} onClick={() => openModal(s)}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-cyan-400 text-slate-950 text-xs font-bold hover:bg-cyan-300 transition-colors">
                      <Send className="w-3.5 h-3.5" /> Talep Oluştur
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div data-testid="svc-my-requests" className="rounded-2xl border border-white/10 bg-slate-900/60 backdrop-blur-xl p-5 sm:p-7">
          <h2 className="text-lg font-semibold text-slate-100 mb-4">Servis Taleplerim</h2>
          {myRequests === null ? (
            <div className="flex justify-center py-6"><div className="w-7 h-7 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" /></div>
          ) : myRequests.length === 0 ? (
            <p className="text-sm text-slate-500">Henüz servis talebiniz yok.</p>
          ) : (
            <div className="space-y-3">
              {myRequests.map((r) => {
                const st = TOW_STATUS[r.status] || TOW_STATUS.bekliyor;
                return (
                  <div key={r.id} data-testid={`svc-my-request-${r.id}`}
                    className="rounded-xl border border-white/10 bg-slate-950/50 px-4 py-3 flex flex-wrap items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold text-sm text-slate-100">{r.business_name}{r.category && ` — ${r.category}`}</p>
                      <p className="text-xs text-slate-500 mt-1">{fmtDate(r.created_at)} • {[r.brand, r.model].filter(Boolean).join(" ") || "Araç"}{r.plate && ` • ${r.plate}`}</p>
                    </div>
                    <span className={`text-xs px-3 py-1.5 rounded-full border font-semibold ${st.cls}`}>{st.label}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {modal && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setModal(null)}>
            <div data-testid="svc-request-modal" className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl bg-slate-900 border border-white/10 shadow-2xl p-6"
              onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-lg font-bold text-white">Servis Talebi — {modal.business_name}</h3>
                <button data-testid="svc-request-modal-close" onClick={() => setModal(null)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
              <form onSubmit={submitRequest} className="space-y-4">
                <div><label className={labelCls}>Aracım</label>
                  <select data-testid="svc-req-vehicle" value={reqForm.vehicleId} onChange={(e) => pickVehicle(e.target.value)} className={inputCls}>
                    <option value="">Manuel giriş / kayıtlı olmayan araç</option>
                    {vehicles.map((v) => (
                      <option key={v.id} value={v.id}>{`${v.brand} ${v.model} — ${v.plate || v.vin}`}</option>
                    ))}
                  </select></div>
                {!reqForm.vehicleId && (
                  <>
                    <div><label className={labelCls}>Şasi No / VIN (opsiyonel)</label>
                      <input data-testid="svc-req-vin" value={reqForm.vin} onChange={(e) => setReqForm({ ...reqForm, vin: e.target.value.toUpperCase() })}
                        className={`${inputCls} font-mono-vin tracking-widest`} /></div>
                    <div className="grid grid-cols-2 gap-4">
                      <div><label className={labelCls}>Marka</label>
                        <input data-testid="svc-req-brand" value={reqForm.brand} onChange={(e) => setReqForm({ ...reqForm, brand: e.target.value })} className={inputCls} /></div>
                      <div><label className={labelCls}>Model</label>
                        <input data-testid="svc-req-model" value={reqForm.model} onChange={(e) => setReqForm({ ...reqForm, model: e.target.value })} className={inputCls} /></div>
                    </div>
                  </>
                )}
                <div><label className={labelCls}>Adres / Konum Notu</label>
                  <input data-testid="svc-req-address" value={reqForm.address} onChange={(e) => setReqForm({ ...reqForm, address: e.target.value })}
                    className={inputCls} placeholder="Aracın getirileceği / bulunduğu adres" /></div>
                <div><label className={labelCls}>Açıklama</label>
                  <textarea data-testid="svc-req-description" rows={3} value={reqForm.description}
                    onChange={(e) => setReqForm({ ...reqForm, description: e.target.value })} className={inputCls}
                    placeholder="Sorun / istenen işlem..." /></div>
                <div>
                  <input ref={fileRef} type="file" accept="image/*" multiple className="hidden"
                    onChange={async (e) => {
                      setUploading(true);
                      try {
                        const paths = await uploadFiles([...e.target.files]);
                        setReqForm((f) => ({ ...f, photos: [...f.photos, ...paths] }));
                      } catch (err) { toast.error(formatApiError(err)); }
                      setUploading(false);
                      e.target.value = "";
                    }} />
                  <button type="button" data-testid="svc-req-upload" onClick={() => fileRef.current?.click()} disabled={uploading}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-dashed border-white/20 text-slate-300 text-sm hover:border-cyan-400/50 hover:text-cyan-300 transition-colors disabled:opacity-50">
                    <Upload className="w-4 h-4" /> {uploading ? "Yükleniyor..." : "Fotoğraf Ekle"}
                  </button>
                  {reqForm.photos.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {reqForm.photos.map((p, i) => (
                        <div key={p} className="relative">
                          <img src={photoUrl(p)} alt="" className="w-16 h-12 object-cover rounded-lg border border-white/10" />
                          <button type="button" onClick={() => setReqForm((f) => ({ ...f, photos: f.photos.filter((_, j) => j !== i) }))}
                            className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-red-500 text-white flex items-center justify-center">
                            <X className="w-2.5 h-2.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <button data-testid="svc-req-submit" type="submit" disabled={sending || uploading}
                  className="w-full py-3 rounded-xl bg-cyan-400 text-slate-950 font-bold hover:bg-cyan-300 transition-all disabled:opacity-50">
                  {sending ? "Gönderiliyor..." : "Talebi Gönder"}
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
